<!--
  设备详情页面组件
  功能：展示单个设备的全部详细信息、相关日志和操作历史。
-->
<template>
  <div class="device-details-page" v-loading="loading">
    <el-page-header @back="goBack" class="page-header">
      <template #content>
        <span class="text-large font-600 mr-3"> 设备详情 </span>
      </template>
    </el-page-header>

    <el-card class="box-card" v-if="device">
      <template #header>
        <div class="card-header">
          <span>{{ device.device_name }} ({{ device.device_id }})</span>
          <div style="display: flex; gap: 12px; align-items: center">
            <el-tag :type="getStatusType(device.status)">{{
              getStatusText(device.status)
            }}</el-tag>
            <el-space>
              <el-button
                type="primary"
                size="small"
                @click="onEdit"
                v-if="hasPermission('device.edit')"
                >编辑</el-button
              >
              <el-button
                type="danger"
                size="small"
                @click="onDelete"
                v-if="hasPermission('device.delete')"
                >删除</el-button
              >
              <el-button
                type="info"
                size="small"
                @click="onClearWifi"
                v-if="hasPermission('device.manage')"
                :disabled="isOffline"
                >清除WiFi</el-button
              >
              <el-button
                type="danger"
                size="small"
                @click="onForceRestart"
                v-if="hasPermission('device.manage')"
                :disabled="isOffline"
                >强制重启</el-button
              >
            </el-space>
          </div>
        </div>
      </template>
      <el-descriptions :column="2" border>
        <el-descriptions-item label="生产序列号">{{
          device.serial_number || "N/A"
        }}</el-descriptions-item>
        <el-descriptions-item label="硬件版本">{{
          device.hardware_version || "N/A"
        }}</el-descriptions-item>
        <el-descriptions-item label="固件版本">{{
          device.firmware_version || "N/A"
        }}</el-descriptions-item>
        <el-descriptions-item label="最后在线时间">{{
          device.last_seen_at ? formatDateTime(device.last_seen_at) : "从未"
        }}</el-descriptions-item>
        <el-descriptions-item label="注册时间">{{
          formatDateTime(device.created_at)
        }}</el-descriptions-item>
        <el-descriptions-item label="License过期时间">{{
          device.license_expires_at
            ? formatDateTime(device.license_expires_at)
            : "N/A"
        }}</el-descriptions-item>
        <el-descriptions-item label="激活IP地址">{{
          device.registration_ip || "N/A"
        }}</el-descriptions-item>
        <el-descriptions-item label="当前上网类型">{{
          readableNetType
        }}</el-descriptions-item>
        <el-descriptions-item label="IMEI">{{
          device.imei || "N/A"
        }}</el-descriptions-item>
        <el-descriptions-item label="IMSI">{{
          device.imsi || "N/A"
        }}</el-descriptions-item>
        <el-descriptions-item label="本月剩余通话时间">
          <span v-if="remainingLoading">加载中...</span>
          <span v-else>
            <template v-if="remainingInfo && remainingInfo.unlimited">
              不限额
            </template>
            <template v-else>
              {{ Math.max(0, remainingInfo?.remaining_minutes || 0) }} 分钟
            </template>
          </span>
        </el-descriptions-item>
        <el-descriptions-item label="本月通话记录">
          <el-button
            type="primary"
            size="small"
            @click="gotoThisMonthCallRecords"
          >
            查看记录
          </el-button>
        </el-descriptions-item>
        <el-descriptions-item label="License密钥" :span="2" v-if="isSuperAdmin">
          <el-input
            v-model="device.license_key"
            type="textarea"
            :rows="3"
            readonly
            placeholder="暂无License密钥"
          />
        </el-descriptions-item>
        <el-descriptions-item label="套餐信息" :span="2" v-if="isSuperAdmin">
          <div class="package-info">
            <el-tooltip
              v-if="devicePackage"
              :content="`${devicePackage.description || '暂无描述'}`"
              placement="top"
              effect="dark"
            >
              <span class="package-name">{{ devicePackage.name }}</span>
            </el-tooltip>
            <span v-else class="package-name">无套餐</span>
          </div>
        </el-descriptions-item>
        <el-descriptions-item label="网络模式偏好" :span="2">
          <el-switch
            v-model="force4g"
            :active-text="'Force 4G'"
            :inactive-text="'Auto'"
            @change="onToggleNetworkMode"
            :disabled="isOffline"
          />
          <div class="network-mode-tip">切换模式会立即重启设备</div>
        </el-descriptions-item>
        <el-descriptions-item label="二维码">
          <div ref="qrCodeEl" class="qr-box"></div>
        </el-descriptions-item>
      </el-descriptions>
    </el-card>
    <el-empty v-else-if="!loading" description="无法加载设备数据"></el-empty>
    <!-- 编辑设备弹窗 -->
    <el-dialog
      v-model="dialogVisible"
      :title="dialogTitle"
      width="600px"
      :close-on-click-modal="false"
    >
      <el-form
        ref="deviceFormRef"
        :model="deviceForm"
        :rules="deviceFormRules"
        label-width="120px"
      >
        <el-form-item label="设备编号" prop="device_id">
          <el-input
            v-model="deviceForm.device_id"
            placeholder="请输入设备编号"
            :disabled="true"
          ></el-input>
        </el-form-item>
        <el-form-item label="设备名称" prop="device_name">
          <el-input
            v-model="deviceForm.device_name"
            placeholder="请输入设备名称"
          ></el-input>
        </el-form-item>
        <el-form-item label="设备型号" prop="device_model">
          <el-input
            v-model="deviceForm.device_model"
            placeholder="请输入设备型号"
          ></el-input>
        </el-form-item>
        <el-form-item label="硬件版本" prop="hardware_version">
          <el-input
            v-model="deviceForm.hardware_version"
            placeholder="请输入硬件版本"
          ></el-input>
        </el-form-item>
        <el-form-item label="固件版本" prop="firmware_version">
          <el-input
            v-model="deviceForm.firmware_version"
            placeholder="请输入固件版本"
          ></el-input>
        </el-form-item>
        <el-form-item label="设备状态" prop="status">
          <el-select v-model="deviceForm.status" placeholder="设备状态">
            <el-option label="在线" value="online" />
            <el-option label="离线" value="offline" />
          </el-select>
        </el-form-item>
        <el-form-item
          label="License状态"
          prop="license_status"
          v-if="isSuperAdmin"
        >
          <el-select
            v-model="deviceForm.license_status"
            placeholder="License状态"
          >
            <el-option label="已激活" value="active" />
            <el-option label="未激活" value="inactive" />
            <el-option label="已过期" value="expired" />
          </el-select>
        </el-form-item>
        <el-form-item
          label="License密钥"
          prop="license_key"
          v-if="isSuperAdmin"
        >
          <el-input
            v-model="deviceForm.license_key"
            type="textarea"
            :rows="3"
            placeholder="请输入License密钥"
          ></el-input>
        </el-form-item>
        <el-form-item label="套餐" prop="package_id" v-if="isSuperAdmin">
          <el-select
            v-model="deviceForm.package_id"
            placeholder="选择套餐"
            style="width: 100%"
          >
            <el-option label="无套餐" :value="0" />
            <el-option
              v-for="pkg in packages"
              :key="pkg.id"
              :label="pkg.name"
              :value="pkg.id"
            />
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="closeDialog">取消</el-button>
          <el-button type="primary" @click="handleSave" :loading="saving"
            >保存</el-button
          >
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, computed, reactive, nextTick } from "vue";
import { useRoute, useRouter } from "vue-router";
import apiClient from "@/services/apiClient";
import config from "@/config";
import { ElMessage, ElMessageBox, FormInstance, FormRules } from "element-plus";
import { logger } from "@/utils/logger";
import { formatDateTime } from "@/utils/dateTimeUtils";
import { useStore } from "vuex";
import { DataService } from "@/services/dataService";

const route = useRoute();
const router = useRouter();

const store = useStore();
const loading = ref(false);
const device = ref<Record<string, any> | null>(null);
const device_id = ref(route.params.id as string);
// 本月剩余通话时间
const remainingLoading = ref(false);
const remainingInfo = ref<any | null>(null);

const fetchRemainingMinutes = async () => {
  if (!device_id.value) return;
  remainingLoading.value = true;
  try {
    const resp = await DataService.getDeviceMonthlyRemaining(device_id.value);
    const payload = (resp as any)?.data;
    if (payload?.code === 0) {
      remainingInfo.value = payload.data || null;
    } else {
      remainingInfo.value = null;
    }
  } catch (e) {
    remainingInfo.value = null;
  } finally {
    remainingLoading.value = false;
  }
};

const gotoThisMonthCallRecords = () => {
  if (!device_id.value) return;
  // 跳转到设备通话明细页，CallRecords 将默认筛选为本月
  router.push({ name: "设备通话明细", params: { device_id: device_id.value } });
};

// 套餐相关
const packages = ref<any[]>([]);
const packagesLoading = ref(false);
const devicePackage = ref<any>(null);

const hasPermission = (perm: string) => store.getters.hasPermission(perm);
const isSuperAdmin = computed(() => store.state.user?.role === "super_admin");

// 是否离线（用于禁用敏感操作）
const isOffline = computed(
  () => String(device.value?.status || "").toLowerCase() !== "online"
);

// 网络类型可读化
const readableNetType = computed(() => {
  const v = String(device.value?.network_type || "").toLowerCase();
  if (v === "wifi") return "WiFi";
  if (["4g", "lte", "mobile", "cellular"].includes(v)) return "4G";
  return "N/A";
});

// 强制4G开关状态
const force4g = ref(false);

// 编辑弹窗状态
const dialogVisible = ref(false);
const dialogTitle = ref("编辑设备");
const saving = ref(false);
const deviceFormRef = ref<FormInstance>();
const deviceForm = reactive({
  id: null,
  device_id: "",
  device_name: "",
  device_model: "",
  hardware_version: "",
  firmware_version: "",
  status: "offline",
  license_status: "inactive",
  license_key: "",
  package_id: 0,
});
const deviceFormRules = reactive<FormRules>({
  device_id: [{ required: true, message: "设备编号不能为空", trigger: "blur" }],
  device_name: [
    { required: true, message: "设备名称不能为空", trigger: "blur" },
  ],
});

const fetchDeviceDetails = async () => {
  if (!device_id.value) {
    console.error("DeviceDetails fetchDeviceDetails - 未提供设备ID");
    ElMessage.error("未提供设备ID");
    return;
  }
  loading.value = true;
  try {
    console.log(
      "DeviceDetails fetchDeviceDetails - 开始获取设备详情，设备ID:",
      device_id.value
    );
    // 注意：需要确保config中有一个可以构建带ID的设备URL的函数
    const url = `${config.apiUrls.DEVICES}/${device_id.value}`;
    console.log("DeviceDetails fetchDeviceDetails - 请求URL:", url);
    const response = await apiClient.get(url);
    console.log("DeviceDetails fetchDeviceDetails - 响应数据:", response.data);
    if (response.data.code === 0) {
      device.value = response.data.data;
      console.log("DeviceDetails fetchDeviceDetails - 设备详情获取成功");
      await fetchQrSourceData(device.value);
      // 初始化网络模式开关（使用网络模式偏好而非当前网络类型）
      const networkModePreference = String(
        device.value?.network_mode_preference || "auto"
      ).toLowerCase();
      force4g.value = networkModePreference === "force_4g";

      // 获取设备套餐信息（仅超级管理员可见，避免非超级管理员触发权限接口）
      if (isSuperAdmin.value) {
        await fetchDevicePackage();
      }
    } else {
      console.error(
        "DeviceDetails fetchDeviceDetails - API返回错误:",
        response.data.message
      );
      ElMessage.error(response.data.message || "获取设备详情失败");
    }
  } catch (error) {
    console.error(
      "DeviceDetails fetchDeviceDetails - 获取设备详情时发生错误:",
      error
    );
    logger.error("获取设备详情时发生错误:", error);
    ElMessage.error("网络错误，无法加载设备详情");
  } finally {
    loading.value = false;
  }
};

const goBack = () => {
  router.push("/deviceManagement");
};

const getStatusType = (
  status: string
): "success" | "warning" | "info" | "danger" => {
  const statusMap: Record<string, "success" | "warning" | "info" | "danger"> = {
    online: "success",
    offline: "warning",
  };
  return statusMap[status] || "info";
};

const getStatusText = (status: string) => {
  const textMap: Record<string, string> = {
    online: "在线",
    offline: "离线",
  };
  return textMap[status] || "未知";
};

onMounted(() => {
  // 清理可能存在的消息通知，避免从其他页面跳转时显示异常消息
  const messageContainer = document.querySelector(".el-message");
  if (messageContainer) {
    messageContainer.remove();
  }

  // 添加调试信息
  console.log("DeviceDetails onMounted - 开始加载设备详情");

  fetchDeviceDetails();
  if (isSuperAdmin.value) {
    console.log("DeviceDetails onMounted - 开始加载套餐列表");
    fetchPackages();
  }
  // 加载本月剩余通话分钟
  fetchRemainingMinutes();
});

// 套餐相关方法
const fetchPackages = async () => {
  try {
    packagesLoading.value = true;
    console.log("DeviceDetails fetchPackages - 开始获取套餐列表");
    const response = await apiClient.get(`${config.apiBaseUrl}/packages`);
    console.log("DeviceDetails fetchPackages - 响应数据:", response.data);
    if (response.data.code === 0) {
      packages.value = response.data.data.items || [];
      console.log(
        "DeviceDetails fetchPackages - 套餐列表获取成功，数量:",
        packages.value.length
      );
    } else {
      console.error(
        "DeviceDetails fetchPackages - API返回错误:",
        response.data.message
      );
      ElMessage.error(response.data.message || "获取套餐列表失败");
    }
  } catch (error) {
    console.error("DeviceDetails fetchPackages - 获取套餐列表失败:", error);
    ElMessage.error("获取套餐列表失败");
  } finally {
    packagesLoading.value = false;
  }
};

// 获取设备套餐信息
const fetchDevicePackage = async () => {
  if (!device.value?.package_id) {
    devicePackage.value = null;
    return;
  }

  try {
    console.log(
      "DeviceDetails fetchDevicePackage - 开始获取设备套餐信息，package_id:",
      device.value.package_id
    );
    const response = await apiClient.get(
      `${config.apiBaseUrl}/packages/${device.value.package_id}`
    );
    console.log("DeviceDetails fetchDevicePackage - 响应数据:", response.data);
    if (response.data.code === 0) {
      devicePackage.value = response.data.data;
      console.log("DeviceDetails fetchDevicePackage - 设备套餐信息获取成功");
    } else {
      console.error(
        "DeviceDetails fetchDevicePackage - API返回错误:",
        response.data.message
      );
      devicePackage.value = null;
    }
  } catch (error) {
    console.error(
      "DeviceDetails fetchDevicePackage - 获取设备套餐信息失败:",
      error
    );
    devicePackage.value = null;
  }
};

const refreshPackages = () => {
  fetchPackages();
};

// 详情页操作
const onClearWifi = async () => {
  if (!device.value) return;
  if (isOffline.value) {
    ElMessage.warning("设备离线，禁止清除WiFi");
    return;
  }
  try {
    await ElMessageBox.confirm(
      `将要清除设备保存的wifi配置 ${device.value.device_name}. 继续?`,
      "确认",
      {
        confirmButtonText: "确认",
        cancelButtonText: "取消",
        type: "warning",
      }
    );
    const resp = await DataService.clearDeviceWifi(device.value.device_id);
    if ((resp as any).data?.code === 0) {
      ElMessage.success("WiFi配置清除成功");
    } else {
      ElMessage.error((resp as any).data?.message || "操作失败");
    }
  } catch {
    ElMessage.info("操作取消");
  }
};

const onForceRestart = async () => {
  if (!device.value) return;
  if (isOffline.value) {
    ElMessage.warning("设备离线，禁止重启操作");
    return;
  }
  try {
    await ElMessageBox.confirm(
      `将要强制重启: ${device.value.device_name}. 继续?`,

      "确认",
      {
        confirmButtonText: "确认",
        cancelButtonText: "取消",
        type: "warning",
      }
    );
    const resp = await DataService.forceDeviceRestart(device.value.device_id);
    if ((resp as any).data?.code === 0) {
      ElMessage.success("已下发重启指令");
    } else {
      ElMessage.error((resp as any).data?.message || "操作失败");
    }
  } catch {
    ElMessage.info("已取消操作");
  }
};

const onToggleNetworkMode = async () => {
  if (!device.value) return;
  if (isOffline.value) {
    // 由于开关被禁用，通常不会触发，但仍然做一次保护
    ElMessage.warning("设备离线，禁止设置网络模式");
    return;
  }
  const mode = force4g.value ? "force_4g" : "auto";
  const resp = await DataService.setDeviceNetworkMode(
    device.value.device_id,
    mode
  );
  if ((resp as any).data?.code === 0) {
    ElMessage.success("网络模式已更新");
  } else {
    ElMessage.error((resp as any).data?.message || "操作失败");
  }
};

// 编辑设备
const onEdit = () => {
  if (!device.value) return;
  dialogTitle.value = "编辑设备";
  // 使用 nextTick 确保表单在数据赋值前已存在
  nextTick(() => {
    Object.assign(deviceForm, device.value);
  });
  dialogVisible.value = true;
};

// 关闭弹窗并重置表单
const closeDialog = () => {
  dialogVisible.value = false;
  deviceFormRef.value?.resetFields();
  // 重置状态
  Object.assign(deviceForm, {
    id: null,
    device_id: "",
    device_name: "",
    device_model: "",
    hardware_version: "",
    firmware_version: "",
    status: "offline",
    license_status: "inactive",
    license_key: "",
    package_id: 0,
  });
};

// 保存设备
const handleSave = async () => {
  if (!deviceFormRef.value) return;
  await deviceFormRef.value.validate(async (valid) => {
    if (valid) {
      saving.value = true;
      try {
        // 编辑模式 - PUT 请求
        const url = `${config.apiUrls.DEVICES}/${deviceForm.device_id}`;
        const response = await apiClient.put(url, deviceForm);

        if (response.data.code === 0) {
          ElMessage.success("设备更新成功");
          closeDialog();
          // 重新获取设备详情以刷新页面数据
          await fetchDeviceDetails();

          // 如果套餐有变化，需要单独更新套餐（仅超级管理员允许修改）
          if (
            isSuperAdmin.value &&
            deviceForm.package_id !== device.value?.package_id
          ) {
            try {
              const actualPackageId =
                deviceForm.package_id === 0 ? null : deviceForm.package_id;
              await apiClient.put(
                `${config.apiBaseUrl}/packages/device/${deviceForm.device_id}/package`,
                { package_id: actualPackageId }
              );
              // 再次刷新设备详情以获取最新的套餐信息
              await fetchDeviceDetails();
            } catch (error) {
              console.error("更新设备套餐失败:", error);
              ElMessage.warning("设备信息更新成功，但套餐更新失败");
            }
          }
        } else {
          ElMessage.error(response.data.message || "操作失败");
        }
      } catch (error: unknown) {
        logger.error("保存设备时出错:", error);
        const errMsg =
          (error as { response?: { data?: { message?: string } } }).response
            ?.data?.message || "网络错误，操作失败";
        ElMessage.error(errMsg);
      } finally {
        saving.value = false;
      }
    }
  });
};

const onDelete = async () => {
  if (!device.value) return;
  try {
    await ElMessageBox.confirm(
      `确定要删除设备 ${device.value.device_name} (${device.value.device_id}) 吗? 这将是一个不可恢复的操作。`,
      "警告",
      {
        confirmButtonText: "确定删除",
        cancelButtonText: "取消",
        type: "warning",
      }
    );
    const url = `${config.apiUrls.DEVICES}/${device.value.device_id}`;
    const response = await apiClient.delete(url);
    if (response.data.code === 0) {
      ElMessage.success("设备删除成功");
      router.push("/deviceManagement");
    } else {
      ElMessage.error(response.data.message || "删除失败");
    }
  } catch {
    ElMessage.info("已取消删除");
  }
};

const qrCodeEl = ref<HTMLElement | null>(null);
const qrCodeInstance = ref<any>(null);
const qrPayload = ref<any | null>(null);

const renderQr = async () => {
  if (!qrCodeEl.value || !qrPayload.value) return;
  const anyWin = window as any;
  if (!anyWin.QRCode) {
    console.error("QRCode 脚本未加载");
    ElMessage.error("二维码脚本未加载");
    return;
  }
  qrCodeEl.value.innerHTML = "";
  qrCodeInstance.value = new anyWin.QRCode(qrCodeEl.value, {
    text: JSON.stringify(qrPayload.value),
    width: 120,
    height: 120,
  });
};

const fetchQrSourceData = async (d: any) => {
  try {
    qrPayload.value = {
      type: 1,
      productId: 147,
      deviceNumber: d.device_id,
      SN: d.serial_number,
      productName: d.device_name || "",
    };
    await nextTick();
    await renderQr();
  } catch (e) {
    console.error("生成二维码失败:", e);
    ElMessage.error("生成二维码失败");
  }
};
</script>

<style lang="scss" scoped>
.device-details-page {
  padding: 20px;
}

.page-header {
  margin-bottom: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.network-mode-tip {
  margin-top: 10px;
  font-size: 12px;
  color: #232425;
}

.package-info {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.package-name {
  font-weight: 500;
  color: #409eff;
}

.qr-box {
  width: 120px;
  height: 120px;
}
</style>
